import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { type Actor, Id } from "../workspace.entity.ts";

// A hostname is an identity. URL parsing supplies the same IDNA conversion in
// browsers and Node; syntax checks precede it so it cannot reinterpret a URL.
export const HostnameSchema = z
	.string()
	.trim()
	.transform((input, ctx) => {
		const value = input.toLowerCase().replace(/\.$/, "");
		if (!value || /[\s/:@?#%\\*]/u.test(value)) {
			ctx.addIssue({
				code: "custom",
				message: "Enter a domain name without a path or port",
			});
			return z.NEVER;
		}
		let hostname: string;
		try {
			hostname = new URL(`https://${value}`).hostname;
		} catch {
			ctx.addIssue({ code: "custom", message: "Enter a valid domain name" });
			return z.NEVER;
		}
		const labels = hostname.split(".");
		if (
			hostname.length > 253 ||
			labels.length < 2 ||
			labels.some(
				(label) => !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label),
			) ||
			/^\d+$/.test(labels.at(-1) ?? "") ||
			/(?:^|\.)(?:localhost|local|internal|web\.app|firebaseapp\.com)$/.test(
				hostname,
			)
		) {
			ctx.addIssue({
				code: "custom",
				message: "Enter your own public domain name",
			});
			return z.NEVER;
		}
		return hostname;
	});
export type Hostname = z.infer<typeof HostnameSchema>;

// Firebase Hosting v1beta1 vocabulary, checked against the official CustomDomain
// resource on 2026-10-18. Native wire parsing stays in the provider adapter.
export const OwnershipStateSchema = z.enum([
	"OWNERSHIP_STATE_UNSPECIFIED",
	"OWNERSHIP_MISSING",
	"OWNERSHIP_UNREACHABLE",
	"OWNERSHIP_MISMATCH",
	"OWNERSHIP_CONFLICT",
	"OWNERSHIP_PENDING",
	"OWNERSHIP_ACTIVE",
]);
export const HostStateSchema = z.enum([
	"HOST_STATE_UNSPECIFIED",
	"HOST_UNHOSTED",
	"HOST_UNREACHABLE",
	"HOST_MISMATCH",
	"HOST_CONFLICT",
	"HOST_ACTIVE",
]);
export const CertificateStateSchema = z.enum([
	"CERT_STATE_UNSPECIFIED",
	"CERT_PREPARING",
	"CERT_VALIDATING",
	"CERT_PROPAGATING",
	"CERT_ACTIVE",
	"CERT_EXPIRING_SOON",
	"CERT_EXPIRED",
]);
export const DnsRecordSchema = z
	.object({
		domainName: z.string().min(1),
		type: z.enum(["TYPE_UNSPECIFIED", "A", "CNAME", "TXT", "AAAA", "CAA"]),
		rdata: z.string(),
		requiredAction: z.enum(["NONE", "ADD", "REMOVE"]),
	})
	.strict();
export type DnsRecord = z.infer<typeof DnsRecordSchema>;
const DnsRecordSetSchema = z
	.object({
		domainName: z.string().min(1),
		records: z.array(DnsRecordSchema),
	})
	.strict();
export const DnsUpdatesSchema = z
	.object({
		discovered: z.array(DnsRecordSetSchema),
		desired: z.array(DnsRecordSetSchema),
		checkTime: z.string().datetime({ offset: true }).nullish(),
	})
	.strict();
export type DnsUpdates = z.infer<typeof DnsUpdatesSchema>;

export const DomainErrorCodeSchema = z.enum([
	"unavailable",
	"not-authorized",
	"dns-pending",
	"provider-unavailable",
	"provider-permission-denied",
	"app-check-capacity",
	"https-pending",
	"cleanup-pending",
]);
export type DomainErrorCode = z.infer<typeof DomainErrorCodeSchema>;

// Clients write intent only. The path owns org/hostname; no second lifecycle
// status or durable browser-test assertion duplicates the observed evidence.
export const DomainIntentSchema = z
	.object({
		desired: z.enum(["connected", "disconnected"]),
		requestId: Id,
		requestedBy: Id,
		requestedAt: z.custom<Timestamp>(),
		makePrimary: z.boolean(),
	})
	.strict();
export type DomainIntent = z.infer<typeof DomainIntentSchema>;
export const DomainSchema = DomainIntentSchema.extend({
	registrationId: Id.nullish(),
	ownershipTxtName: z.string().min(1).nullish(),
	ownershipTxtValue: z.string().min(1).nullish(),
	ownershipCheckedAt: z.custom<Timestamp>().nullish(),
	ownershipVerified: z.boolean().nullish(),
	hostingResource: z.string().min(1).nullish(),
	hostingEtag: z.string().min(1).nullish(),
	operationName: z.string().min(1).nullish(),
	ownershipState: OwnershipStateSchema.nullish(),
	hostState: HostStateSchema.nullish(),
	certificateState: CertificateStateSchema.nullish(),
	dnsUpdates: DnsUpdatesSchema.nullish(),
	providerCheckedAt: z.custom<Timestamp>().nullish(),
	appCheckState: z.enum(["pending", "configured", "error"]).nullish(),
	appCheckCheckedAt: z.custom<Timestamp>().nullish(),
	httpsCheckedAt: z.custom<Timestamp>().nullish(),
	httpsObservedOrgId: Id.nullish(),
	managedPolicyEntries: z
		.object({
			appCheck: z.array(HostnameSchema),
			auth: z.array(HostnameSchema),
		})
		.strict()
		.nullish(),
	cleanup: z
		.object({
			hosting: z.boolean(),
			operation: z.boolean(),
			appCheck: z.boolean(),
			auth: z.boolean(),
		})
		.strict()
		.nullish(),
	safeError: z
		.object({ code: DomainErrorCodeSchema, retryable: z.boolean() })
		.strict()
		.nullish(),
	updatedAt: z.custom<Timestamp>().nullish(),
});
export type Domain = z.infer<typeof DomainSchema>;

// Policy is authored by the permission owner; absent declarations fail closed.
export const Domain = entity<Actor>()({
	schema: DomainSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "workspace.domains.manage"),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "workspace.domains.manage"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/domains/{hostname}",
		}),
	);
